/**
 * KeyRoleSlotCard — one canonical loan role, filled or not.
 *
 * The slot is always rendered so an unfilled role stays on the board, and both
 * states share one skeleton so they are the same height:
 *
 *   [status]  Role name                           [actions]
 *             Contact name        (filled)   or   "Not assigned" (empty)
 *             Company · phone
 *
 * Filled  → a check in a green disc, and three actions: email, change (opens
 *           the assign dialog on this role) and remove (confirmed by the caller).
 * Empty   → a dashed sand card with a dashed disc and one neutral "Assign"
 *           button (contactsTokens holds the palette).
 *
 * The card BODY is the click target (it opens the contact's edit panel); the
 * actions sit outside it, so a nested button never happens and clicking an
 * action never also opens the panel.
 */
import AddRounded from "@mui/icons-material/AddRounded";
import CheckRounded from "@mui/icons-material/CheckRounded";
import CloseRounded from "@mui/icons-material/CloseRounded";
import MailOutlineRounded from "@mui/icons-material/MailOutlineRounded";
import RemoveRounded from "@mui/icons-material/RemoveRounded";
import SwapHorizRounded from "@mui/icons-material/SwapHorizRounded";
import { Box, Button, IconButton, Tooltip } from "@mui/material";
import PropTypes from "prop-types";
import { memo } from "react";

import { formatPhone } from "../../../../../_src_shared/utils/functions/formatPhone";
import Flx from "../../../../../components/layout/Flx";
import Txt from "../../../../../components/typography/Txt";
import { TAB_TOKENS as T } from "../../../shared/tabLayout/tabTokens";
import { BOX_LINE, MISSING } from "./contactsTokens";

export const contactDisplayName = (contact) =>
  `${contact?.firstName || ""} ${contact?.lastName || ""}`.trim() ||
  contact?.company ||
  contact?.emailAddress ||
  "Unnamed contact";

/** Small square action button — the card's own icon-button size. */
const actionSx = {
  width: 28,
  height: 28,
  borderRadius: T.radius.xs,
  color: T.color.muted,
  "&:hover": { background: "#eef2f7", color: T.color.ink2 },
};

const KeyRoleSlotCard = ({
  role,
  contact,
  extraCount = 0,
  selected,
  onOpen,
  onAssign,
  onRemove,
}) => {
  const filled = Boolean(contact);
  const name = contactDisplayName(contact);
  const phone = contact?.phone ? formatPhone(contact.phone) : null;
  const email = contact?.emailAddress || null;
  const sub = [contact?.company, phone].filter(Boolean).join("  ·  ");

  return (
    <Flx
      ac
      sx={{
        gap: "12px",
        padding: "11px 10px 11px 13px",
        // Fills the grid cell its wrapper stretches to, so every slot in a row
        // is the same height whether it holds two lines or three.
        flex: 1,
        width: "100%",
        minHeight: 70,
        borderRadius: "11px",
        // A sand wash for an unfilled role, not the full warn fill — a gap to
        // notice, not an error (see contactsTokens).
        background: filled ? T.color.surface : MISSING.fill,
        border: `1px ${filled ? "solid" : "dashed"} ${
          filled
            ? selected
              ? T.color.brand
              : BOX_LINE
            : MISSING.line
        }`,
        boxShadow: selected ? `0 0 0 3px ${T.color.brandSoft}` : "none",
        transition: "border-color 150ms ease",
        "@media (prefers-reduced-motion: reduce)": { transition: "none" },
        "&:hover": {
          borderColor: filled ? T.color.borderStrong : MISSING.lineHover,
        },
      }}
    >
      {/* status disc */}
      <Flx
        center
        sx={{
          flexShrink: 0,
          width: 26,
          height: 26,
          borderRadius: "50%",
          ...(filled
            ? { background: T.color.okSoft, color: T.color.okInk }
            : {
                color: MISSING.ink,
                border: `1.5px dashed ${MISSING.line}`,
              }),
        }}
      >
        {filled ? (
          <CheckRounded sx={{ fontSize: 16 }} />
        ) : (
          <RemoveRounded sx={{ fontSize: 15 }} />
        )}
      </Flx>

      {/* body — the click target when filled */}
      <Box
        component={filled ? "button" : "div"}
        type={filled ? "button" : undefined}
        onClick={filled ? onOpen : undefined}
        aria-label={filled ? `Open ${role}: ${name}` : undefined}
        sx={{
          font: "inherit",
          textAlign: "left",
          background: "none",
          border: 0,
          padding: 0,
          minWidth: 0,
          flex: 1,
          cursor: filled ? "pointer" : "default",
          "&:focus-visible": {
            outline: `2px solid ${T.color.brand}`,
            outlineOffset: "2px",
            borderRadius: "6px",
          },
        }}
      >
        <Flx ac sx={{ gap: "6px", minWidth: 0 }}>
          <Txt
            sx={{
              fontSize: "11.5px",
              color: T.color.ink3,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {role}
          </Txt>
          {extraCount > 0 && (
            <Tooltip
              title={`${extraCount} more contact${
                extraCount === 1 ? "" : "s"
              } on this loan carries the ${role} role — all of them are in All contacts.`}
            >
              <Txt
                sx={{
                  fontSize: "10.5px",
                  fontWeight: 600,
                  color: T.color.brand,
                  flexShrink: 0,
                  cursor: "help",
                }}
              >
                +{extraCount} more
              </Txt>
            </Tooltip>
          )}
        </Flx>

        <Txt
          sx={{
            display: "block",
            fontSize: "14.5px",
            fontWeight: filled ? 600 : 500,
            letterSpacing: "-0.01em",
            color: filled ? T.color.ink : MISSING.ink,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {filled ? name : "Not assigned"}
        </Txt>

        {filled && sub ? (
          <Txt
            sx={{
              display: "block",
              fontSize: "12px",
              color: T.color.ink3,
              fontVariantNumeric: "tabular-nums",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {sub}
          </Txt>
        ) : null}
      </Box>

      {/* actions */}
      {filled ? (
        <Flx ac sx={{ gap: "2px", flexShrink: 0 }}>
          {email && (
            <Tooltip title={`Email ${name}`}>
              <IconButton
                size="small"
                component="a"
                href={`mailto:${email}`}
                aria-label={`Email ${name}`}
                sx={actionSx}
              >
                <MailOutlineRounded sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
          <Tooltip title={`Change ${role}`}>
            <IconButton
              size="small"
              onClick={onAssign}
              aria-label={`Change ${role}`}
              sx={actionSx}
            >
              <SwapHorizRounded sx={{ fontSize: 17 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title={`Remove ${name} from this loan`}>
            <IconButton
              size="small"
              onClick={onRemove}
              aria-label={`Remove ${name}`}
              sx={{
                ...actionSx,
                "&:hover": {
                  background: T.color.dangerSoft,
                  color: T.color.dangerInk,
                },
              }}
            >
              <CloseRounded sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        </Flx>
      ) : (
        // The theme's neutral outlined button, untouched. An amber-bordered
        // button read as a second warning beside the card's own — the card
        // already says the role is empty; the button just has to be pressable.
        <Button
          size="small"
          variant="outlined"
          color="gray"
          onClick={onAssign}
          startIcon={<AddRounded sx={{ fontSize: "16px !important" }} />}
          sx={{
            flexShrink: 0,
            textTransform: "none",
            fontSize: 12.5,
            minHeight: 30,
            background: T.color.surface,
          }}
        >
          Assign
        </Button>
      )}
    </Flx>
  );
};

KeyRoleSlotCard.propTypes = {
  /** Canonical role name — must match selectOptionsContactRole to prefill. */
  role: PropTypes.string.isRequired,
  /** The assigned contact, or null for an unfilled slot. */
  contact: PropTypes.object,
  /** How many OTHER contacts also carry this role. */
  extraCount: PropTypes.number,
  /** True while this contact is open in the edit sidebar. */
  selected: PropTypes.bool,
  /** Open the contact's edit panel. */
  onOpen: PropTypes.func,
  /** Open the assign / change dialog for this role. */
  onAssign: PropTypes.func,
  /** Ask to remove the contact (the caller confirms). */
  onRemove: PropTypes.func,
};

export default memo(KeyRoleSlotCard);
